import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFitStore } from '../../store/useFitStore';
import { supabase } from '../../lib/supabase';
import type { BillingInterval, Client, ClientLifecycle, ClientStatus, CheckIn, Session, CoachingNote } from '../../store/types';
import { fmtSets } from '../../lib/sets';
import { billClient, describeBilling, billingToneColor } from '../../lib/billing';
import { planPriceLabel } from '../../lib/coachingPlans';
import {
  BODY_ZONE_LABELS,
  CATEGORY_META,
  CATEGORY_ORDER,
  TRIAGE_META,
  TRIAGE_STATUSES,
  type BodyMapCategory,
  type BodyZoneId,
  type TriageStatus,
} from '../../lib/bodyMap';
import PhotosTab from './ClientPhotos';
import NoteContent from '../shared/NoteContent';

/* ============================================================
   ClientHub — the per-client workspace, opened from the Planner
   client header. Full-screen: on desktop the CRM profile sits
   in a persistent left column while Notes / History / Check-in
   use the wide right side; on mobile it's one column with tabs.
   Replaced the old narrow right rail (clipped fields, wasted
   screen) and the standalone ClientDetail page before it.
============================================================ */

type Tab = 'profile' | 'notes' | 'history' | 'checkin' | 'photos' | 'advisor';
const TAB_LABELS: Record<Tab, string> = { profile: 'Profile', notes: 'Notes', history: 'History', checkin: 'Check-in', photos: 'Photos', advisor: '✦ Advisor' };

const LIFECYCLES: ClientLifecycle[] = ['lead', 'active', 'paused', 'churned'];
// Triage status — labels mirror the Overview dashboard (On Track / Needs Check / High Risk).
const STATUSES: { key: ClientStatus; label: string }[] = [
  { key: 'green', label: 'On track' },
  { key: 'yellow', label: 'Needs check' },
  { key: 'red', label: 'High risk' },
];

const fmtDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso.length <= 10 ? iso + 'T00:00:00' : iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const relativeDay = (iso: string): string => {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return fmtDate(iso);
};

// ISO Monday of the week containing `d` (local time, no UTC drift).
export const mondayOf = (d: Date): string => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

interface Props {
  client: Client;
  onClose: () => void;
}

const ClientHub: React.FC<Props> = ({ client, onClose }) => {
  const { user, checkIns, upsertCheckIn, deleteCheckIn, updateClientCrm, addCoachingNote, deleteCoachingNote, showToast } = useFitStore();
  // Desktop shows the profile column persistently, so the right side opens on
  // Notes; mobile keeps Profile as the first tab.
  const [tab, setTab] = useState<Tab>(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'notes' : 'profile'
  );
  const navigate = useNavigate();
  const openLog = () => { onClose(); navigate(`/coach/client/${client.id}/log`); };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ padding: 0, alignItems: 'stretch', justifyContent: 'stretch' }}>
      <div
        className="bg-fit-bg-secondary h-full w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header — one row, the rest of the screen belongs to content */}
        <div className="px-5 lg:px-8 py-4 border-b border-fit-line flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full bg-fit-bg-tertiary flex items-center justify-center text-[14px] font-bold shrink-0">
              {client.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </span>
            <div className="min-w-0">
              <h2 className="text-[19px] font-bold tracking-tight leading-tight truncate">{client.name}</h2>
              <p className="text-[12px] text-fit-text-secondary truncate">{client.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={openLog} className="btn btn-glass btn-sm">
              Training log ↗
            </button>
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-xl leading-none px-1" aria-label="Close">×</button>
          </div>
        </div>

        {/* body — persistent profile column on desktop, tabs for the rest */}
        <div className="flex-1 min-h-0 lg:grid lg:grid-cols-[400px_1fr]">
          {/* profile column (desktop) */}
          <div className="hidden lg:block overflow-y-auto no-scrollbar border-r border-fit-line p-6">
            <p className="mono-label mb-4">Profile</p>
            <ProfileFields client={client} updateClientCrm={updateClientCrm} />
          </div>

          {/* tabbed area */}
          <div className="h-full min-h-0 flex flex-col">
            {/* one row, always — overflow scrolls instead of wrapping */}
            <div className="flex items-center gap-1 px-5 lg:px-8 pt-3 pb-2 border-b border-fit-line overflow-x-auto no-scrollbar">
              {(['profile', 'notes', 'history', 'checkin', 'photos', 'advisor'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 transition-colors ${
                    t === 'profile' ? 'lg:hidden ' : ''
                  }${tab === t ? 'bg-white text-fit-ink' : 'text-fit-text-secondary hover:text-white'}`}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-5 lg:p-8">
              <div className="max-w-[820px]">
                {tab === 'profile' && (
                  <div className="lg:hidden">
                    <ProfileFields client={client} updateClientCrm={updateClientCrm} />
                  </div>
                )}
                {(tab === 'notes' || (tab === 'profile' && typeof window !== 'undefined' && window.innerWidth >= 1024)) && (
                  <NotesPanel client={client} addCoachingNote={addCoachingNote} deleteCoachingNote={deleteCoachingNote} />
                )}
                {tab === 'history' && <HistoryTab clientId={client.id} onOpenLog={openLog} />}
                {tab === 'checkin' && (
                  <CheckInTab clientId={client.id} coachId={user?.id || client.coachId || ''} checkIns={checkIns} upsertCheckIn={upsertCheckIn} deleteCheckIn={deleteCheckIn} showToast={showToast} />
                )}
                {tab === 'photos' && <PhotosTab client={client} showToast={showToast} />}
                {tab === 'advisor' && <AdvisorTab client={client} showToast={showToast} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- shared bits ---------------- */

// Textarea that grows with its content — nothing to scroll inside, nothing clipped.
const AutoTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ style, ...props }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} {...props} style={{ ...style, overflow: 'hidden', resize: 'none' }} />;
};

// Long notes (imported diet protocols…) collapse to a preview; paragraph
// structure is preserved so they read like documents, not word soup.
const NoteCard: React.FC<{ note: CoachingNote; onDelete: () => void }> = ({ note, onDelete }) => (
  <div className="rounded-fit-md border border-fit-line p-3.5">
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <span className="text-[10px] uppercase tracking-[0.08em] text-fit-muted">{relativeDay(note.createdAt)}</span>
      <button onClick={onDelete} className="text-fit-muted hover:text-white text-[12px] leading-none">×</button>
    </div>
    <NoteContent text={note.text} collapsedLines={6} />
  </div>
);

/* ---------------- Profile (CRM) ---------------- */

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-4">
    <p className="mono-label mb-1.5">{label}</p>
    {children}
  </div>
);

const ProfileFields: React.FC<{
  client: Client;
  updateClientCrm: ReturnType<typeof useFitStore.getState>['updateClientCrm'];
}> = ({ client, updateClientCrm }) => {
  // Local drafts committed on blur, so typing doesn't thrash the store.
  const [phone, setPhone] = useState(client.phone || '');
  const [startDate, setStartDate] = useState(client.startDate || '');
  const [goals, setGoals] = useState(client.goals || '');
  const [injuries, setInjuries] = useState(client.injuries || '');

  // App access — invite a managed client (no login yet) to the app.
  const { inviteClient, updateClientStatus } = useFitStore();
  const [inviteEmail, setInviteEmail] = useState(client.email || '');
  const [inviting, setInviting] = useState(false);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  const onInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    const res = await inviteClient(client.id, email);
    setInviting(false);
    if (res) setInviteSentTo(res.email);
  };

  // Baseline captured at mount (ClientHub is keyed by client id, so this resets per
  // client). Blur compares the draft against this — not the live prop — so a server
  // snapshot arriving mid-edit can't be clobbered by a no-op blur.
  const baseline = useRef({ phone: client.phone || '', startDate: client.startDate || '', goals: client.goals || '', injuries: client.injuries || '' });

  const commit = (patch: Partial<Pick<Client, 'phone' | 'startDate' | 'goals' | 'injuries'>>) => {
    Object.assign(baseline.current, patch);
    updateClientCrm(client.id, patch);
  };

  return (
    <div>
      {/* app access — invite to the app (creates + links a login) */}
      <Field label="App access">
        {inviteSentTo ? (
          <div className="rounded-fit-md border border-fit-line p-3 text-[12px] leading-relaxed">
            <p className="mb-1 font-semibold text-white">Invitation email sent</p>
            <p className="text-fit-text-secondary">
              {inviteSentTo} can use the secure link to choose a password and join your roster.
            </p>
          </div>
        ) : client.userId ? (
          <p className="text-[13px]">✓ Has a login <span className="text-fit-muted">· {client.email || '—'}</span></p>
        ) : (
          <div className="flex items-start gap-2">
            <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="client@email.com" className="input" />
            <button onClick={onInvite} disabled={inviting || !inviteEmail.trim()} className="btn btn-primary btn-sm shrink-0 disabled:opacity-40">
              {inviting ? 'Inviting…' : 'Invite'}
            </button>
          </div>
        )}
      </Field>

      {/* triage status — feeds the Overview dashboard counts */}
      <Field label="Status">
        <div className="flex items-center gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => updateClientStatus(client.id, s.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                client.status === s.key ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Field>

      {/* lifecycle */}
      <Field label="Lifecycle">
        <div className="flex items-center gap-1 flex-wrap">
          {LIFECYCLES.map((l) => (
            <button
              key={l}
              onClick={() => updateClientCrm(client.id, { lifecycle: l })}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] capitalize transition-colors ${
                client.lifecycle === l ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => phone !== baseline.current.phone && commit({ phone })}
            placeholder="—"
            className="input"
          />
        </Field>
        <Field label="Start date">
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); updateClientCrm(client.id, { startDate: e.target.value }); }}
            className="input"
          />
        </Field>
      </div>

      <Field label="Subscription">
        <p className="text-[13px] capitalize">{client.subscriptionStatus || 'none'}</p>
      </Field>

      <PricingBlock client={client} />

      <Field label="Goals">
        <AutoTextarea
          value={goals}
          onChange={(e) => setGoals(e.target.value)}
          onBlur={() => goals !== baseline.current.goals && commit({ goals })}
          placeholder="What is this client working toward?"
          className="input"
          style={{ minHeight: 64 }}
        />
      </Field>

      <Field label="Injuries / limitations">
        <AutoTextarea
          value={injuries}
          onChange={(e) => setInjuries(e.target.value)}
          onBlur={() => injuries !== baseline.current.injuries && commit({ injuries })}
          placeholder="Anything to program around?"
          className="input"
          style={{ minHeight: 64 }}
        />
      </Field>

      <BodyMapBlock client={client} />
    </div>
  );
};

/* ---------------- Pricing & billing (plan enrolment + Stripe checkout) ----------------
   Enrolling in a plan COPIES its rate onto the client profile (migration 0018);
   the stripe-billing edge function bills from that copied rate, so a plan never
   needs its own Stripe Product/Price. The status line reads the webhook-synced
   client_billing cache. */

const RATE_INTERVALS: { key: BillingInterval; label: string }[] = [
  { key: 'month', label: '/mo' },
  { key: 'week', label: '/wk' },
  { key: 'session', label: '/session' },
  { key: 'year', label: '/yr' },
];

const PricingBlock: React.FC<{ client: Client }> = ({ client }) => {
  const { user, coachingPlans, clientBilling, updateClientCrm, reloadBilling, showToast } = useFitStore();
  const [rateDraft, setRateDraft] = useState(client.priceCents != null ? String(client.priceCents / 100) : '');
  const [billing, setBilling] = useState(false);

  // Keep the draft in step when enrolment copies a new rate onto the profile.
  useEffect(() => {
    setRateDraft(client.priceCents != null ? String(client.priceCents / 100) : '');
  }, [client.priceCents]);

  const row = clientBilling.find((b) => b.clientId === client.id) || null;
  const display = describeBilling(row);

  // Offered plans, plus the enrolled one even if since hidden.
  const plans = coachingPlans.filter((p) => p.isActive || p.id === client.planId);
  const enrolled = coachingPlans.find((p) => p.id === client.planId);

  const enroll = (planId: string) => {
    if (!planId) {
      // Clear the enrolment; the copied rate stays so billing is unaffected.
      updateClientCrm(client.id, { planId: undefined });
      return;
    }
    const plan = coachingPlans.find((p) => p.id === planId);
    if (!plan) return;
    updateClientCrm(client.id, {
      planId: plan.id,
      priceCents: plan.priceCents,
      billingInterval: plan.billingInterval,
      currency: plan.currency,
    });
  };

  const commitRate = () => {
    const raw = rateDraft.replace(/[$,\s]/g, '');
    if (!raw) return;
    const n = parseFloat(raw);
    if (isNaN(n) || n < 0) { setRateDraft(client.priceCents != null ? String(client.priceCents / 100) : ''); return; }
    const cents = Math.round(n * 100);
    if (cents !== client.priceCents) updateClientCrm(client.id, { priceCents: cents, currency: client.currency || 'usd' });
  };

  const bill = async () => {
    if (!user?.stripeAccountId) { showToast('Connect Stripe first — Profile → Payments', 'error'); return; }
    if (!client.priceCents || client.priceCents <= 0) { showToast('Set a rate for this client first', 'error'); return; }
    setBilling(true);
    const res = await billClient(client.id);
    setBilling(false);
    if (!res.ok) { showToast(res.error, 'error'); return; }
    window.open(res.url, '_blank', 'noopener');
    try { await navigator.clipboard.writeText(res.url); showToast('Checkout opened — link copied to share'); } catch { showToast('Checkout link opened'); }
    reloadBilling(); // pick up the pending client_billing row
  };

  return (
    <Field label="Pricing & billing">
      <div className="space-y-2.5">
        {plans.length > 0 && (
          <select value={client.planId || ''} onChange={(e) => enroll(e.target.value)} className="input">
            <option value="">No plan — custom rate</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {planPriceLabel(p)}{p.isActive ? '' : ' (hidden)'}
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-1.5">
          <span className="text-[13px] text-fit-text-secondary">$</span>
          <input
            value={rateDraft}
            onChange={(e) => setRateDraft(e.target.value)}
            onBlur={commitRate}
            inputMode="decimal"
            placeholder="Rate"
            className="input"
            style={{ maxWidth: 110 }}
          />
          <div className="flex items-center gap-1 flex-wrap">
            {RATE_INTERVALS.map((i) => (
              <button
                key={i.key}
                onClick={() => updateClientCrm(client.id, { billingInterval: i.key })}
                className={`px-2 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                  client.billingInterval === i.key ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>
        {enrolled && client.priceCents != null && client.priceCents !== enrolled.priceCents && (
          <p className="text-[11px] text-fit-muted">Custom rate — differs from the {enrolled.name} plan price.</p>
        )}

        <p className="text-[12px] inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: billingToneColor(display.tone) }} />
          <span className="text-fit-text-secondary">{display.text}</span>
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={bill} disabled={billing} className="btn btn-primary btn-sm disabled:opacity-40">
            {billing ? 'Creating checkout…' : 'Bill via Stripe'}
          </button>
          {row?.hostedInvoiceUrl && (row.status === 'checkout_created' || row.status === 'past_due') && (
            <a href={row.hostedInvoiceUrl} target="_blank" rel="noreferrer" className="text-[12px] text-fit-text-secondary hover:text-white underline underline-offset-2">
              Payment link ↗
            </a>
          )}
        </div>
      </div>
    </Field>
  );
};

/* ---------------- Body map (client-authored zones + coach triage) ---------------- */

const SeverityDots: React.FC<{ value?: number; color: string }> = ({ value, color }) => {
  if (typeof value !== 'number') return null;
  return (
    <span className="inline-flex items-center gap-0.5 ml-1.5 align-middle">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className="w-1.5 h-1.5 rounded-full" style={{ background: n <= value ? color : 'var(--fit-line, #2a2a2a)' }} />
      ))}
    </span>
  );
};

const BodyMapBlock: React.FC<{ client: Client }> = ({ client }) => {
  const { user, bodyMapAssessments, bodyMapTriage, upsertBodyMapTriage } = useFitStore();
  const [showHistory, setShowHistory] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const mine = useMemo(
    () => bodyMapAssessments.filter((a) => a.clientId === client.id).sort((a, b) => b.assessedOn.localeCompare(a.assessedOn)),
    [bodyMapAssessments, client.id]
  );
  const latest = mine[0];
  const coachId = user?.id || client.coachId || '';

  const key = (zoneId: BodyZoneId, category: BodyMapCategory) => `${zoneId}:${category}`;
  const triageFor = (zoneId: BodyZoneId, category: BodyMapCategory) =>
    bodyMapTriage.find((t) => t.clientId === client.id && t.zoneId === zoneId && t.category === category);

  const writeTriage = (zoneId: BodyZoneId, category: BodyMapCategory, patch: { status?: TriageStatus; coachNote?: string }) => {
    const cur = triageFor(zoneId, category);
    upsertBodyMapTriage({
      id: cur?.id || crypto.randomUUID(),
      clientId: client.id,
      coachId,
      zoneId,
      category,
      status: patch.status ?? cur?.status ?? 'active',
      coachNote: patch.coachNote !== undefined ? (patch.coachNote.trim() || undefined) : cur?.coachNote,
      updatedAt: new Date().toISOString(),
    });
  };

  const cycleStatus = (zoneId: BodyZoneId, category: BodyMapCategory) => {
    const cur = triageFor(zoneId, category);
    const idx = cur ? TRIAGE_STATUSES.indexOf(cur.status) : -1;
    writeTriage(zoneId, category, { status: TRIAGE_STATUSES[(idx + 1) % TRIAGE_STATUSES.length] });
  };

  if (!latest || latest.marks.length === 0) {
    return (
      <Field label="Body map">
        <p className="text-[13px] text-fit-muted">No areas flagged by the client yet.</p>
      </Field>
    );
  }

  const groups = CATEGORY_ORDER
    .map((cat) => ({ cat, items: latest.marks.filter((m) => m.category === cat).sort((a, b) => BODY_ZONE_LABELS[a.zoneId].localeCompare(BODY_ZONE_LABELS[b.zoneId])) }))
    .filter((g) => g.items.length > 0);

  return (
    <Field label={`Body map · ${fmtDate(latest.assessedOn)}`}>
      <div className="space-y-3">
        {latest.note ? <p className="text-[12px] text-fit-text-secondary italic">“{latest.note}”</p> : null}
        {groups.map((g) => (
          <div key={g.cat}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_META[g.cat].color }} />
              <span className="mono-label">{CATEGORY_META[g.cat].label}</span>
            </div>
            <div className="space-y-2">
              {g.items.map((m) => {
                const tri = triageFor(m.zoneId, m.category);
                const k = key(m.zoneId, m.category);
                const draft = noteDrafts[k] ?? tri?.coachNote ?? '';
                const statusColor = tri ? TRIAGE_META[tri.status].color : 'var(--fit-text-secondary, #9a9a9e)';
                return (
                  <div key={k} className="rounded-fit-md border border-fit-line p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold">
                        {BODY_ZONE_LABELS[m.zoneId]}
                        <SeverityDots value={m.severity} color={CATEGORY_META[m.category].color} />
                      </span>
                      <button
                        onClick={() => cycleStatus(m.zoneId, m.category)}
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] border whitespace-nowrap"
                        style={{ borderColor: statusColor, color: statusColor }}
                        title="Tap to cycle: active → monitoring → cleared"
                      >
                        {tri ? TRIAGE_META[tri.status].label : 'Triage'}
                      </button>
                    </div>
                    {m.note ? <p className="text-[12px] text-fit-text-secondary mt-1">{m.note}</p> : null}
                    <input
                      value={draft}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [k]: e.target.value }))}
                      onBlur={() => { if (draft !== (tri?.coachNote ?? '')) writeTriage(m.zoneId, m.category, { coachNote: draft }); }}
                      placeholder="Coach note (e.g. cleared for squats)"
                      className="input mt-2"
                      style={{ fontSize: 12 }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {mine.length > 1 ? (
          <div>
            <button onClick={() => setShowHistory((v) => !v)} className="text-[12px] text-fit-muted hover:text-white">
              {showHistory ? 'Hide' : 'Show'} earlier maps ({mine.length - 1})
            </button>
            {showHistory ? (
              <div className="mt-1.5 space-y-1">
                {mine.slice(1).map((a) => (
                  <p key={a.id} className="text-[12px] text-fit-text-secondary">
                    {fmtDate(a.assessedOn)} — {a.marks.length} area{a.marks.length === 1 ? '' : 's'}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Field>
  );
};

/* ---------------- ✦ Advisor (premium — free during beta) ----------------
   Ask analytical questions about THIS client; the ai-advisor edge function
   correlates deterministic facts (schedule patterns, PR timeline, check-ins,
   notes) and writes a report that persists as the client's knowledge base. */

interface AdvisorReport { id: string; question: string; report: string; createdAt: string }

const SUGGESTED_QUESTIONS = [
  'Which weekly training schedule produced the best results?',
  'When did PRs cluster, and what lined up with them?',
  'How is body composition trending vs diet adherence?',
];

// Minimal markdown for advisor reports: # title, **bold**, - bullets, [links](url).
const mdBold = (s: string, keyBase: string): React.ReactNode[] =>
  s.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={`${keyBase}b${i}`} className="text-white font-semibold">{part}</strong> : part));

const mdInline = (s: string): React.ReactNode[] =>
  s.split(/(\[[^\]]+\]\(https?:\/\/[^)]+\))/g).flatMap((part, i): React.ReactNode[] => {
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
    if (link) {
      return [
        <a key={`l${i}`} href={link[2]} target="_blank" rel="noreferrer" className="underline underline-offset-2 text-fit-text-secondary hover:text-white">
          {link[1]}
        </a>,
      ];
    }
    return mdBold(part, `s${i}`);
  });

const Md: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex flex-col gap-1.5">
    {text.split('\n').map((line, i) => {
      const t = line.trim();
      if (!t) return null;
      if (t.startsWith('# ')) return <p key={i} className="text-[15px] font-bold tracking-tight">{t.slice(2)}</p>;
      if (/^#{2,4}\s/.test(t)) return <p key={i} className="text-[13px] font-bold mt-1">{mdInline(t.replace(/^#+\s/, ''))}</p>;
      if (/^[-•]\s/.test(t)) return <p key={i} className="text-[13px] text-fit-text-secondary leading-relaxed pl-3">• {mdInline(t.slice(2))}</p>;
      return <p key={i} className="text-[13px] text-fit-text-secondary leading-relaxed">{mdInline(t)}</p>;
    })}
  </div>
);

const ReportCard: React.FC<{ r: AdvisorReport; defaultOpen?: boolean }> = ({ r, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-fit-md border border-fit-line p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold">{r.question}</p>
          <span className="text-[10px] uppercase tracking-[0.08em] text-fit-muted shrink-0">
            {relativeDay(r.createdAt)} {open ? '↑' : '↓'}
          </span>
        </div>
      </button>
      {open && <div className="mt-3 pt-3 border-t border-fit-line"><Md text={r.report} /></div>}
    </div>
  );
};

const AdvisorTab: React.FC<{
  client: Client;
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ client, showToast }) => {
  const [view, setView] = useState<'chat' | 'reports'>('chat');
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1">
          {(['chat', 'reports'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                view === v ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
              }`}
            >
              {v === 'chat' ? '✦ Chat' : 'Reports'}
            </button>
          ))}
        </div>
        <span className="text-[9px] uppercase tracking-[0.1em] text-fit-text-secondary border border-fit-line-strong rounded-full px-2 py-0.5">
          Premium · free during beta
        </span>
      </div>
      {view === 'chat'
        ? <AdvisorChat client={client} showToast={showToast} />
        : <AdvisorReports client={client} showToast={showToast} />}
    </div>
  );
};

/* ---------- Advisor chat (the Oura-style conversation) ---------- */

interface ChatMsg { id: string; role: 'user' | 'assistant'; content: string; createdAt: string }

const CHAT_STARTERS = [
  'How has he been progressing lately?',
  'What should next week look like?',
  'Anything in the data I should worry about?',
];

const AdvisorChat: React.FC<{
  client: Client;
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ client, showToast }) => {
  const { user } = useFitStore();
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [research, setResearch] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const first = client.name.split(' ')[0];

  useEffect(() => {
    let on = true;
    supabase
      .from('advisor_messages')
      .select('id, role, content, created_at')
      .eq('client_id', client.id)
      .order('created_at', { ascending: true })
      .limit(200)
      .then(({ data }) => {
        if (!on) return;
        setMsgs((data || []).map((m) => ({ id: m.id, role: m.role, content: m.content, createdAt: m.created_at })));
        setLoaded(true);
      });
    return () => { on = false; };
  }, [client.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length, busy]);

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    // optimistic — the server persists the real rows
    setMsgs((all) => [...all, { id: `tmp_${Date.now()}`, role: 'user', content: text, createdAt: new Date().toISOString() }]);
    const { data, error } = await supabase.functions.invoke('ai-advisor', {
      body: { clientId: client.id, question: text, research, mode: 'chat' },
    });
    setBusy(false);
    if (error || !data?.ok) {
      const msg = data?.reason === 'limit'
        ? 'Daily chat limit reached — back tomorrow'
        : data?.reason === 'no_data'
          ? 'Not enough data yet — log sessions or import history first'
          : 'Advisor unavailable — try again';
      showToast(msg, 'error');
      return;
    }
    const r = data.reply;
    setMsgs((all) => [...all, { id: r.id, role: 'assistant', content: r.content, createdAt: r.created_at }]);
  };

  const saveToReports = async (content: string) => {
    if (!user) return;
    const { error } = await supabase.from('client_reports').insert({
      id: crypto.randomUUID(),
      coach_id: user.id,
      client_id: client.id,
      question: 'Saved from chat',
      report: content,
      created_at: new Date().toISOString(),
    });
    showToast(error ? 'Could not save' : 'Saved to reports', error ? 'error' : undefined);
  };

  const clearChat = async () => {
    await supabase.from('advisor_messages').delete().eq('client_id', client.id);
    setMsgs([]);
  };

  return (
    <div className="flex flex-col" style={{ minHeight: 360 }}>
      {/* thread */}
      <div className="flex-1 flex flex-col gap-3 mb-4">
        {loaded && msgs.length === 0 && (
          <div className="py-6">
            <p className="text-[14px] font-semibold mb-1">Ask me anything about {first}.</p>
            <p className="text-[13px] text-fit-text-secondary mb-3">
              I've read the whole file — every session, PR, and check-in. Conversations stay here, and any answer can be saved to {first}'s reports.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CHAT_STARTERS.map((s) => (
                <button key={s} onClick={() => send(s)} disabled={busy} className="px-2.5 py-1 rounded-full text-[11px] bg-fit-bg-tertiary text-fit-text-secondary hover:text-white transition-colors disabled:opacity-40">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="self-end max-w-[85%] rounded-fit-md bg-white/10 px-3.5 py-2.5">
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.content}</p>
            </div>
          ) : (
            <div key={m.id} className="self-start max-w-[92%] rounded-fit-md border border-fit-line px-3.5 py-3">
              <Md text={m.content} />
              <button
                onClick={() => saveToReports(m.content)}
                className="text-[10px] uppercase tracking-[0.08em] text-fit-muted hover:text-white mt-2"
              >
                Save to reports ↓
              </button>
            </div>
          )
        )}
        {busy && (
          <div className="self-start inline-flex items-center gap-2 text-[12px] text-fit-text-secondary px-1">
            <span className="w-[6px] h-[6px] rounded-full bg-white animate-pulse" />
            {research ? 'Checking the data and the research…' : 'Reading the data…'}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* composer */}
      <div className="flex items-end gap-2">
        <AutoTextarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
          }}
          placeholder={`Ask about ${first}…`}
          className="input"
          style={{ minHeight: 44 }}
        />
        <button onClick={() => send(input)} disabled={busy || !input.trim()} className="btn btn-primary btn-sm shrink-0 disabled:opacity-40">
          Send
        </button>
      </div>
      <div className="flex items-center justify-between mt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer text-[11px] text-fit-text-secondary hover:text-white">
          <input type="checkbox" checked={research} onChange={() => setResearch((v) => !v)} className="accent-white" />
          <span><span className="font-semibold">Research</span> — search studies & cite sources</span>
        </label>
        {msgs.length > 0 && (
          <button onClick={clearChat} className="text-[11px] text-fit-muted hover:text-white underline underline-offset-2">
            Clear conversation
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------- Advisor reports (the saved knowledge base) ---------- */

const AdvisorReports: React.FC<{
  client: Client;
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ client, showToast }) => {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [research, setResearch] = useState(false);
  const [reports, setReports] = useState<AdvisorReport[]>([]);
  const [loaded, setLoaded] = useState(false);

  // The client's knowledge base — every past report, newest first.
  useEffect(() => {
    let on = true;
    supabase
      .from('client_reports')
      .select('id, question, report, created_at')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        if (!on) return;
        setReports((data || []).map((r) => ({ id: r.id, question: r.question, report: r.report, createdAt: r.created_at })));
        setLoaded(true);
      });
    return () => { on = false; };
  }, [client.id]);

  const ask = async (q: string) => {
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('ai-advisor', {
      body: { clientId: client.id, question: text, research },
    });
    setBusy(false);
    if (error || !data) { showToast('Advisor unavailable — try again', 'error'); return; }
    if (!data.ok) {
      const msg = data.reason === 'limit'
        ? 'Daily advisor limit reached (10 reports/day)'
        : data.reason === 'no_data'
          ? 'Not enough data yet — log sessions or import history first'
          : 'Advisor unavailable — try again';
      showToast(msg, 'error');
      return;
    }
    const r = data.report;
    setReports((all) => [{ id: r.id, question: r.question, report: r.report, createdAt: r.created_at }, ...all]);
    setQuestion('');
  };

  return (
    <div>
      <p className="text-[13px] text-fit-text-secondary leading-relaxed mb-3">
        One-shot deep dives on {client.name.split(' ')[0]}'s data — they save here as the knowledge base,
        alongside anything you keep from chat.
      </p>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {SUGGESTED_QUESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => ask(s)}
            disabled={busy}
            className="px-2.5 py-1 rounded-full text-[11px] bg-fit-bg-tertiary text-fit-text-secondary hover:text-white transition-colors disabled:opacity-40"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex items-start gap-2 mb-2">
        <AutoTextarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Did the Mon/Wed/Fri weeks beat the Mon/Fri/Sun weeks?"
          className="input"
          style={{ minHeight: 56 }}
        />
        <button onClick={() => ask(question)} disabled={busy || !question.trim()} className="btn btn-primary btn-sm shrink-0 disabled:opacity-40">
          {busy ? 'Analyzing…' : 'Ask'}
        </button>
      </div>
      <label className="inline-flex items-center gap-2 mb-5 cursor-pointer text-[12px] text-fit-text-secondary hover:text-white">
        <input type="checkbox" checked={research} onChange={() => setResearch((v) => !v)} className="accent-white" />
        <span>
          <span className="font-semibold">Include research</span> — searches published studies and cites sources (slower)
        </span>
      </label>
      {busy && (
        <p className="text-[12px] text-fit-text-secondary mb-4 inline-flex items-center gap-2">
          <span className="w-[6px] h-[6px] rounded-full bg-white animate-pulse" />
          {research
            ? `Analyzing ${client.name.split(' ')[0]}'s data and searching the literature — up to a minute…`
            : `Reading ${client.name.split(' ')[0]}'s history — schedule, PRs, check-ins…`}
        </p>
      )}

      {loaded && reports.length === 0 && !busy && (
        <p className="text-[12px] text-fit-muted">No reports yet — ask the first question.</p>
      )}
      <div className="flex flex-col gap-2.5">
        {reports.map((r, i) => <ReportCard key={r.id} r={r} defaultOpen={i === 0} />)}
      </div>
    </div>
  );
};

/* ---------------- Coaching notes (timeline) ---------------- */

const NotesPanel: React.FC<{
  client: Client;
  addCoachingNote: ReturnType<typeof useFitStore.getState>['addCoachingNote'];
  deleteCoachingNote: ReturnType<typeof useFitStore.getState>['deleteCoachingNote'];
}> = ({ client, addCoachingNote, deleteCoachingNote }) => {
  const [noteDraft, setNoteDraft] = useState('');
  const addNote = () => {
    if (!noteDraft.trim()) return;
    addCoachingNote(client.id, noteDraft);
    setNoteDraft('');
  };

  return (
    <div>
      <p className="mono-label mb-2">Coaching notes</p>
      <div className="flex items-start gap-2 mb-4">
        <AutoTextarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Add a note — context, observations, follow-ups…"
          className="input"
          style={{ minHeight: 56 }}
        />
        <button onClick={addNote} disabled={!noteDraft.trim()} className="btn btn-primary btn-sm shrink-0 disabled:opacity-40">Add</button>
      </div>

      {(client.coachingNotes?.length ?? 0) === 0 ? (
        <p className="text-[12px] text-fit-muted">No notes yet.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {[...client.coachingNotes!].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((n) => (
            <NoteCard key={n.id} note={n} onDelete={() => deleteCoachingNote(client.id, n.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- History (logged sessions + feedback) ---------------- */

// Sessions are the source of truth for training history (the same model the
// Planner board and Training Log read). The legacy workoutLogs feed only the
// form-video/feedback flow below, when any exist.
const HistoryTab: React.FC<{ clientId: string; onOpenLog: () => void }> = ({ clientId, onOpenLog }) => {
  // Self-sources from the store so this (and the exercise-name index it needs)
  // only mounts/builds when the coach actually opens the History tab.
  const { workoutLogs, masterPrograms, globalExercises, sessions, addFeedback, showToast } = useFitStore();
  const recent = useMemo<Session[]>(
    () =>
      sessions
        .filter((s) => s.clientId === clientId && (s.status === 'completed' || s.status === 'partial'))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 8),
    [sessions, clientId]
  );
  const logs = useMemo(
    () => workoutLogs.filter((l) => l.clientId === clientId).sort((a, b) => b.date.localeCompare(a.date)),
    [workoutLogs, clientId]
  );
  // Workout logs are keyed by exercise id with no name; resolve ids to names from
  // every exercise source, falling back to the key (covers demo logs keyed by name).
  const exNameById = useMemo(() => {
    const m: Record<string, string> = {};
    globalExercises.forEach((g) => { m[g.id] = g.name; });
    masterPrograms.forEach((p) =>
      p.workouts.forEach((w) => w.exercises.forEach((e) => { m[e.id] = e.name; }))
    );
    // Session-based logs are keyed by the session's PlannedExercise ids (workout_id = session.id).
    sessions.forEach((s) => s.sections.forEach((sec) => sec.exercises.forEach((pe) => { m[pe.id] = pe.name; })));
    return m;
  }, [globalExercises, masterPrograms, sessions]);
  const [fb, setFb] = useState<Record<string, string>>({});

  const send = async (logId: string, exId: string, setIdx: number) => {
    const key = `${logId}_${exId}_${setIdx}`;
    const text = fb[key]?.trim();
    if (!text) return;
    try {
      await addFeedback(logId, exId, setIdx, text);
      setFb((m) => ({ ...m, [key]: '' }));
      showToast('Feedback sent');
    } catch (e: any) {
      showToast(e?.message || 'Could not send feedback', 'error');
    }
  };

  if (recent.length === 0 && logs.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-[14px] font-semibold mb-1">No logged sessions yet</p>
        <p className="text-[12px] text-fit-muted">Completed workouts and form-check videos will appear here.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* recent training (sessions model — same data as the board & Training Log) */}
      {recent.map((s) => (
        <div key={s.id} className="rounded-fit-md border border-fit-line p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[14px] font-bold">
              {s.title}
              {s.status === 'partial' && <span className="text-fit-muted font-normal text-[11px]"> · partial</span>}
            </p>
            <p className="mono-label">{fmtDate(s.date)}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            {s.sections.flatMap((sec) => sec.exercises).slice(0, 6).map((ex) => (
              <div key={ex.id} className="flex items-baseline justify-between gap-3 border-b border-fit-line pb-1 last:border-0">
                <p className="text-[13px] truncate">{ex.name}</p>
                <p className="text-[12px] text-fit-text-secondary tabular-nums shrink-0">
                  {fmtSets(ex.sets, ex.measure, ex.unit) || '—'}
                </p>
              </div>
            ))}
            {s.sections.reduce((n, sec) => n + sec.exercises.length, 0) > 6 && (
              <p className="text-[11px] text-fit-muted">
                +{s.sections.reduce((n, sec) => n + sec.exercises.length, 0) - 6} more exercises
              </p>
            )}
          </div>
        </div>
      ))}
      <button onClick={onOpenLog} className="btn btn-glass btn-sm w-full">
        Full history · charts · check-ins ↗
      </button>

      {/* legacy client-app logs (form videos + per-set feedback), when any exist */}
      {logs.length > 0 && <p className="mono-label mt-2">Form checks & feedback</p>}
      {logs.map((log) => (
        <div key={log.id} className="rounded-fit-md border border-fit-line p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[14px] font-bold">{log.workoutTitle}</p>
            <p className="mono-label">{fmtDate(log.date)}</p>
          </div>
          <div className="flex flex-col gap-3">
            {Object.entries(log.exercises).map(([exId, sets]) => (
              <div key={exId}>
                <div className="flex items-baseline justify-between gap-3 border-b border-fit-line pb-1.5">
                  <p className="text-[13px] font-semibold truncate">{exNameById[exId] || exId}</p>
                  <p className="text-[12px] text-fit-text-secondary tabular-nums shrink-0">
                    {sets.map((s) => `${s.weight}×${s.reps}`).join('  ·  ')}
                  </p>
                </div>
                {sets.map((s, i) => (
                  <div key={i} className="mt-1.5">
                    {s.formVideoUrl && (
                      <video src={s.formVideoUrl} controls playsInline preload="metadata" style={{ width: '100%', maxWidth: 260, borderRadius: 8, marginTop: 6, border: '1px solid var(--glass-border)' }} />
                    )}
                    {s.coachFeedback ? (
                      <p className="text-[11px] text-fit-muted">↳ set {i + 1}: “{s.coachFeedback}”</p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          value={fb[`${log.id}_${exId}_${i}`] || ''}
                          onChange={(e) => setFb((m) => ({ ...m, [`${log.id}_${exId}_${i}`]: e.target.value }))}
                          placeholder={`Feedback · set ${i + 1}…`}
                          className="input"
                        />
                        <button onClick={() => send(log.id!, exId, i)} className="btn btn-glass btn-sm shrink-0">Send</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Check-in (weekly review ritual) ---------------- */

const num = (v: string): number | undefined => {
  const n = parseFloat(v);
  return isNaN(n) ? undefined : n;
};
const str = (n?: number | null) => (n == null ? '' : String(n));

const CheckInTab: React.FC<{
  clientId: string;
  coachId: string;
  checkIns: CheckIn[];
  upsertCheckIn: ReturnType<typeof useFitStore.getState>['upsertCheckIn'];
  deleteCheckIn: ReturnType<typeof useFitStore.getState>['deleteCheckIn'];
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ clientId, coachId, checkIns, upsertCheckIn, deleteCheckIn, showToast }) => {
  const weekOf = mondayOf(new Date());
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const mine = useMemo(
    () => checkIns.filter((c) => c.clientId === clientId).sort((a, b) => b.weekOf.localeCompare(a.weekOf)),
    [checkIns, clientId]
  );
  const thisWeek = mine.find((c) => c.weekOf === weekOf);

  // Draft seeded from this week's existing check-in (edit) or blank (new).
  const [weight, setWeight] = useState(str(thisWeek?.weightLbs));
  const [bf, setBf] = useState(str(thisWeek?.bodyFatPct));
  const [smm, setSmm] = useState(str(thisWeek?.skeletalMuscleMass));
  const [sleep, setSleep] = useState(thisWeek?.sleep || '');
  const [diet, setDiet] = useState(thisWeek?.diet || '');
  const [recovery, setRecovery] = useState(thisWeek?.recovery || '');
  const [notes, setNotes] = useState(thisWeek?.notes || '');

  const save = () => {
    upsertCheckIn({
      id: thisWeek?.id || crypto.randomUUID(),
      clientId,
      coachId: thisWeek?.coachId || coachId,
      weekOf,
      weightLbs: num(weight),
      bodyFatPct: num(bf),
      skeletalMuscleMass: num(smm),
      sleep: sleep.trim() || undefined,
      diet: diet.trim() || undefined,
      recovery: recovery.trim() || undefined,
      notes: notes.trim() || undefined,
      createdAt: thisWeek?.createdAt || new Date().toISOString(),
    });
    showToast(thisWeek ? 'Check-in updated' : 'Check-in saved');
  };

  // History with week-over-week weight delta (oldest→newest to compute deltas).
  const history = useMemo(() => {
    const asc = [...mine].sort((a, b) => a.weekOf.localeCompare(b.weekOf));
    const withDelta = asc.map((c, i) => ({
      ci: c,
      delta: i > 0 && c.weightLbs != null && asc[i - 1].weightLbs != null ? c.weightLbs - asc[i - 1].weightLbs! : undefined,
    }));
    return withDelta.reverse();
  }, [mine]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="mono-label">This week · {fmtDate(weekOf)}</p>
        <span className={`text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 rounded-full ${thisWeek ? 'bg-white text-fit-ink' : 'border border-fit-line-strong text-fit-text-secondary'}`}>
          {thisWeek ? 'Logged' : 'Due'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Field label="Weight (lb)"><input value={weight} onChange={(e) => setWeight(e.target.value)} inputMode="decimal" placeholder="—" className="input" /></Field>
        <Field label="Body fat %"><input value={bf} onChange={(e) => setBf(e.target.value)} inputMode="decimal" placeholder="—" className="input" /></Field>
        <Field label="Muscle (lb)"><input value={smm} onChange={(e) => setSmm(e.target.value)} inputMode="decimal" placeholder="—" className="input" /></Field>
      </div>
      <Field label="Sleep"><input value={sleep} onChange={(e) => setSleep(e.target.value)} placeholder="Quality / hours" className="input" /></Field>
      <Field label="Diet"><input value={diet} onChange={(e) => setDiet(e.target.value)} placeholder="Adherence this week" className="input" /></Field>
      <Field label="Recovery"><input value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="Aches, energy, stress" className="input" /></Field>
      <Field label="Notes"><AutoTextarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to carry into next week's plan" className="input" style={{ minHeight: 56 }} /></Field>

      <button onClick={save} className="btn btn-primary btn-sm w-full mt-1">{thisWeek ? 'Update check-in' : 'Save check-in'}</button>

      {/* past check-ins */}
      <div className="mt-6 pt-5 border-t border-fit-line">
        <p className="mono-label mb-2">History</p>
        {history.length === 0 ? (
          <p className="text-[12px] text-fit-muted">No check-ins yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {history.map(({ ci, delta }) => (
              <div key={ci.id} className="rounded-fit-md border border-fit-line p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-fit-muted">Week of {fmtDate(ci.weekOf)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold tabular-nums">
                      {ci.weightLbs != null ? `${ci.weightLbs} lb` : '—'}
                      {delta !== undefined && delta !== 0 && (
                        <span className="text-fit-muted font-normal"> ({delta > 0 ? '+' : ''}{delta.toFixed(1)})</span>
                      )}
                    </span>
                    {confirmDel === ci.id ? (
                      <span className="flex items-center gap-1.5">
                        <button
                          onClick={() => { deleteCheckIn(ci.id); showToast('Check-in deleted'); setConfirmDel(null); }}
                          className="text-[11px] font-semibold text-white underline underline-offset-2"
                        >
                          Delete
                        </button>
                        <button onClick={() => setConfirmDel(null)} className="text-[11px] text-fit-muted hover:text-white">Cancel</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmDel(ci.id)}
                        className="text-fit-muted hover:text-white text-sm leading-none"
                        title="Delete check-in"
                        aria-label="Delete check-in"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-fit-text-secondary">
                  {ci.bodyFatPct != null && <span>BF {ci.bodyFatPct}%</span>}
                  {ci.skeletalMuscleMass != null && <span>SMM {ci.skeletalMuscleMass}kg</span>}
                  {ci.sleep && <span>· sleep: {ci.sleep}</span>}
                  {ci.recovery && <span>· recovery: {ci.recovery}</span>}
                </div>
                {ci.notes && <p className="text-[11px] text-fit-muted mt-1">{ci.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientHub;
